import React from 'react';

import { isArray, isEmpty } from 'lodash';

import {
  ArrayField,
  DateField,
  RecordContextProvider,
  useRecordContext,
  SelectField,
  FunctionField,
  useRedirect,
  DatagridConfigurable,
  SelectColumnsButton,
  usePermissions,
} from 'react-admin';
import { Route, Routes } from 'react-router-dom';

import {
  Alert,
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';

import { ArrowForwardIosOutlined } from '@mui/icons-material';

import useEntityData from '../../../../../hooks/useEntityData';

import actions, { Actions } from '../../../../../constants/actions';
import { boldDataGridStyle } from '../../../../../constants/style/datagridStyles';

import ImageField from '../../../../../customFields/ImageField';
import { ViewJSONDialog } from '../../../../../customFields/ViewJSON';

import NoResults from '../../../../layout/NoResults';
import PurchaseButton from '../../../../layout/button/PurchaseButton';

import EntityData from '../EntityData';

import person from '../../../../../assets/exposure/person.png';
import { PepMatches } from '../../../../../utilities/schemas/pepMatches';
import { relatedEntityRelationsText } from '../../../../../constants/relatedEntityRelations';
import ColoredScoreField from '../../../../../customFields/ColoredScoreField';
import { countryChoices } from '../../../../../constants/countries';
import languages from '../../../../../constants/languages';
import PepDetails from './details';
import hasAccess from '../../../../../utilities/hasAccess';
import PermissionMissing from '../../../../utils/PermissionMissing';

const RedirectToEntityButton = ({ label }: { label?: string }) => {
  const redirect = useRedirect();
  const record = useRecordContext<NonNullable<PepMatches['potentialMatches'][number]>>();
  if (isEmpty(record)) return null;
  return (
    <IconButton onClick={() => redirect(`${record._id}`)} aria-label={label}>
      <ArrowForwardIosOutlined />
    </IconButton>
  );
};

const PepBody = ({
  data,
  handlePurchase,
  isLoading,
}: {
  data: PepMatches | undefined;
  handlePurchase: () => void;
  isLoading: boolean;
}) => {
  const { permissions } = usePermissions<Actions[]>();

  return (
    <RecordContextProvider value={data}>
      <Card variant="outlined">
        <CardHeader
          title="Politically exposed person (PEP) records"
          subheader={(
            hasAccess(permissions, actions.INDIVIDUAL_GET_BUSINESS_ASSOCIATION) && (
              <Box display="flex" alignItems="baseline">
                <Typography>Latest update:</Typography>
                <DateField sx={{ marginLeft: 1, fontWeight: 'bold' }} source="createdAt" emptyText="-" showTime />
              </Box>
            )
          )}
          action={(
            <Box display="flex" gap={4}>
              {hasAccess(permissions, actions.INDIVIDUAL_GET_BUSINESS_ASSOCIATION) && (<SelectColumnsButton color="secondary" />)}
              {hasAccess(permissions, actions.INDIVIDUAL_GET_BUSINESS_ASSOCIATION) && (<ViewJSONDialog color="secondary" title="Inspect" />)}
              <PurchaseButton
                loading={isLoading}
                label="Get PEP records"
                onClick={handlePurchase}
                empty={!isArray(data?.potentialMatches)}
                action={actions.INDIVIDUAL_CREATE_PEP}
                icon={null}
                color="secondary"
              />
            </Box>
          )}
        />
        <Divider />
        {data?.isTruncated && (
          <Grid container justifyContent="center" marginTop={2}>
            <Alert severity="warning">This screening report contains truncated results because there were too many potential matches.</Alert>
          </Grid>
        )}
        {hasAccess(permissions, actions.INDIVIDUAL_GET_BUSINESS_ASSOCIATION) ? (
          <ArrayField source="potentialMatches">
            <DatagridConfigurable empty={<NoResults variant="h6" />} sx={{ ...boldDataGridStyle, width: '100%' }} bulkActionButtons={false}>
              <FunctionField
                label="Name"
                sx={{ whiteSpace: 'pre' }}
                render={(record: PepMatches['potentialMatches'][number]) => {
                  if (!isEmpty(record.fullName)) return record.fullName;
                  const name = `${record.firstName ?? ''} ${record.lastName ?? ''}`;
                  return isEmpty(name.trim()) ? '-' : name;
                }}
              />
              <ColoredScoreField source="matchProbability" label="Match %" />
              <DateField source="birthDate" options={{ timeZone: 'UTC' }} label="Birth date" />
              <SelectField source="address.country" choices={countryChoices(languages.EN)} label="Country" />
              <FunctionField
                label="Role description"
                sx={{ whiteSpace: 'pre' }}
                render={(record: PepMatches['potentialMatches'][number]) => {
                  if (record.pepRoles.length === 0) return '-';
                  return record.pepRoles
                    .map((role) => role.roleDescription ?? role.role)
                    .slice(0, 5).join('\n');
                }}
              />
              <FunctionField
                label="Relations"
                sx={{ whiteSpace: 'pre' }}
                render={(record: PepMatches['potentialMatches'][number]) => {
                  if (record.relatedEntities.length === 0) return '-';
                  return record.relatedEntities
                    .map((relatedEntity) => [
                      relatedEntity.entityName,
                      relatedEntity.relationshipType
                        ? relatedEntityRelationsText[relatedEntity.relationshipType] : undefined,
                    ].filter((text) => !!text).join(' - '))
                    .slice(0, 5).join('\n');
                }}
              />
              <ImageField label="Image" source="profileImages[0]" emptyImage={person} />
              <RedirectToEntityButton />
            </DatagridConfigurable>
          </ArrayField>
        ) : (
          <Box margin={5} display="flex" width="100%" alignItems="center" flexDirection="column">
            <PermissionMissing />
          </Box>
        )}
      </Card>
    </RecordContextProvider>
  );
};

const Pep = () => {
  const {
    data,
    handlePurchase,
    isLoading,
  } = useEntityData<PepMatches>({
    source: 'pep',
    getAction: actions.INDIVIDUAL_GET_PEP,
  });

  return (
    <Routes>
      <Route
        index
        element={(
          <Slide in direction="right" appear={false}>
            <Grid container spacing={6}>
              <Grid size={12}>
                <EntityData />
              </Grid>
              <Grid size={12}>
                <PepBody data={data} handlePurchase={handlePurchase} isLoading={isLoading} />
              </Grid>
            </Grid>
          </Slide>
        )}
      />
      <Route path=":pepId" element={<PepDetails />} />
    </Routes>
  );
};

export default Pep;
